import {
  Button,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Skeleton,
} from '@newfront-insurance/core-ui/v2';
import { X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import type { ShareGroupsResult } from './use-custom-skill-group-shares';
import type { SkillShareGroup } from '../../../../../queries/skill-group-sharing';
import { useUserNovaGroups } from '../../../hooks/use-user-nova-groups';
import { useNovaContext } from '../../../providers';
import { useSelectionSubmission } from '../../settings-shared/use-selection-submission';

/** The group picker only adds access. Existing shares stay checked and disabled. */
export function SkillGroupShareDialog({
  sharedGroups,
  onClose,
  onShare,
}: {
  sharedGroups: SkillShareGroup[];
  onClose: () => void;
  onShare: (groups: SkillShareGroup[]) => Promise<ShareGroupsResult>;
}) {
  const { userUuid } = useNovaContext();
  const { groups: discoveredGroups, status, retry } = useUserNovaGroups(userUuid);
  const loading = status === 'loading';
  const error = status === 'error';
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [confirmed, setConfirmed] = useState<Set<string>>(() => new Set());
  const checkboxId = useId();
  const groups = useMemo(
    () =>
      [...new Map([...discoveredGroups, ...sharedGroups].map((group) => [group.uuid, group])).values()].sort(
        (a, b) => Number(b.isOrganization) - Number(a.isOrganization) || a.name.localeCompare(b.name),
      ),
    [discoveredGroups, sharedGroups],
  );
  const existing = new Set([...sharedGroups.map((group) => group.uuid), ...confirmed]);
  const selectedGroups = groups.filter((group) => selected.has(group.uuid) && !existing.has(group.uuid));
  const normalizedFilter = filter.trim().toLocaleLowerCase();
  const visibleGroups = groups.filter((group) => group.name.toLocaleLowerCase().includes(normalizedFilter));
  const initialLoading = loading;

  const {
    isSaving: saving,
    error: saveError,
    submit,
    close,
  } = useSelectionSubmission({
    selected: selectedGroups,
    getKey: (group) => group.uuid,
    disabled: loading || error,
    onSubmit: async (items) => {
      const result = await onShare(items);
      return { failedKeys: result.failedGroupUuids, error: result.error };
    },
    onSettled: (failed, succeeded) => {
      setSelected(new Set(failed.map((group) => group.uuid)));
      setConfirmed((current) => new Set([...current, ...succeeded.map((group) => group.uuid)]));
    },
    onClose,
    partialFailureMessage: "Couldn't share with these groups. Your remaining selections are kept. Try again.",
    failureMessage: "Couldn't share this skill. Your selections are kept. Try again.",
  });

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        showCloseButton={false}
        className="flex w-[640px] max-w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden p-0 font-sans"
      >
        <DialogHeader className="border-steel-75 shrink-0 flex-row items-center justify-between border-b px-5 py-4">
          <DialogTitle className="text-steel-500 text-lg font-normal">Share skill</DialogTitle>
          <DialogDescription className="sr-only">
            Select teams or organizations to share this skill with. Existing shares are already selected.
          </DialogDescription>
          <DialogClose asChild>
            <Button
              type="button"
              variant="transparent"
              className="text-steel-400 h-7 w-7 p-1"
              aria-label="Close sharing"
              disabled={saving}
            >
              <X aria-hidden="true" className="size-5" />
            </Button>
          </DialogClose>
        </DialogHeader>
        <div className="border-steel-75 shrink-0 border-b px-5 py-3">
          <Input
            type="search"
            aria-label="Filter teams and organizations"
            placeholder="Filter by keyword..."
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            disabled={saving}
          />
        </div>
        <div className="min-h-0 overflow-y-auto" aria-busy={initialLoading}>
          {initialLoading ? (
            <div role="status" aria-label="Loading teams and organizations" className="space-y-6 px-5 py-4">
              {[0, 1, 2].map((index) => (
                <Skeleton key={index} className="bg-steel-100 h-6 w-full" />
              ))}
            </div>
          ) : error ? (
            <p role="alert" className="text-fire-500 px-5 py-5 text-sm">
              Couldn't load teams and organizations.{' '}
              <Button type="button" variant="inline" onClick={retry}>
                Retry
              </Button>
            </p>
          ) : visibleGroups.length ? (
            <ul aria-label="Teams and organizations" className="max-h-[min(360px,50dvh)] overflow-y-auto">
              {visibleGroups.map((group) => {
                const alreadyShared = existing.has(group.uuid);
                const id = `${checkboxId}-${group.uuid}`;
                return (
                  <li key={group.uuid} className="border-steel-75 border-b last:border-b-0">
                    <label
                      htmlFor={id}
                      className="flex min-h-16 cursor-pointer items-center gap-3 px-5 py-3 has-[:disabled]:cursor-default"
                    >
                      <Checkbox
                        id={id}
                        aria-label={`Share with ${group.name}`}
                        className="size-4"
                        checked={alreadyShared || selected.has(group.uuid)}
                        disabled={saving || alreadyShared}
                        onCheckedChange={(checked) => {
                          setSelected((current) => {
                            const next = new Set(current);
                            if (checked === true) next.add(group.uuid);
                            else next.delete(group.uuid);
                            return next;
                          });
                        }}
                      />
                      <span className="text-steel-500 min-w-0 flex-1 text-base font-semibold break-words">
                        {group.name}
                      </span>
                      <span className="text-steel-400 shrink-0 text-right text-sm">
                        {group.isOrganization ? 'Organization' : 'Team'}
                        {alreadyShared ? <span className="block text-xs">Already shared</span> : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p role="status" className="text-steel-400 px-5 py-5 text-sm">
              {normalizedFilter
                ? 'No teams or organizations match your search.'
                : 'No teams or organizations are available to share with.'}
            </p>
          )}
        </div>
        {saveError ? (
          <p
            role="alert"
            className="text-fire-500 max-h-24 shrink-0 overflow-y-auto px-5 py-3 text-sm whitespace-pre-line"
          >
            {saveError}
          </p>
        ) : null}
        <DialogFooter className="border-steel-75 shrink-0 border-t px-5 py-4">
          <DialogClose asChild>
            <Button type="button" variant="secondary" disabled={saving}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            disabled={loading || Boolean(error) || saving || !selectedGroups.length}
            loading={saving}
            onClick={() => void submit()}
          >
            Share with {selectedGroups.length} selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
